// @flow

import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { routeTo } from 'redux-router-kit';

import { isInIframe } from '@zapier/common-utils';
import { makeQueryString, urlWithNext } from '@zapier/url-utils';

import { Profile } from '@zapier/shared-entities';

import Link from 'app/common/components/Link';
import LinkPreserveParamsContainer from 'app/common/components/LinkPreserveParamsContainer';
import LoginActions from 'app/login/LoginActions';
import LoginDifferent from 'app/login/components/LoginDifferent';
import LoginForm from 'app/login/components/LoginForm';
import MaybeLoad from 'app/common/components/MaybeLoad';
import ResultTypes from 'app/login/ResultTypes';
import TrackActions from 'app/track/TrackActions';
import hocify from 'app/common/components/hocify';
import maybeRender from 'app/common/components/maybeRender';
import { CAPTCHA_REQUIRED_FIELD_ERROR } from 'app/login/constants';
import { maybeUnsafeRouteOptions } from 'app/common/utils/RouterUtils';
import {
  SIGNUP_TYPE_EMAIL,
  SIGNUP_TYPE_FACEBOOK,
  SIGNUP_TYPE_GOOGLE,
  SIGNUP_TYPE_MICROSOFT,
} from 'app/track/constants';
import {
  formattedMessageForResult,
  toFieldErrorsObj,
} from 'app/login/LoginUtils';
import {
  hasLastSessionTimedOut,
  selectCurrentUrl,
  selectSelectedApis,
  selectUseCaseSlug,
} from 'app/login/Selectors';
import { isBadCredentials, isValidLogin } from 'app/login/submitResults';

const DEFAULT_LOGIN_URL = '/app/dashboard';

const ContinueDescription = ({ email }: { email: string }) => (
  <p>You are currently logged in as {email}.</p>
);

const HelpfulLoggedOut = () => (
  <p>
    <a href="/app/login/forgot">Forgot password?</a>
  </p>
);

/*
 * If a user visits the LoginForm, present at `/app/login` after having their session expired,
 * they will see either "Continue with Google" (with no email/password form) or a pre-filled
 * email/password form (with no "Continue with Google" button). To provide the ability for them
 * to either login with SSO or email, we need to display an optional CTA with the prompt to
 * display the whole login form.
 */
const signonCta = (shouldShowEntireForm: boolean, signonIdentity: Object) => {
  // shouldShowEntireForm indicates that the *whole* login form is being displayed, so there is
  // no need to render the CTA.
  if (shouldShowEntireForm) {
    return null;
  }

  const hasPreviousSignon = Boolean(signonIdentity?.email);

  // if the user doesn't have a previous signon identity, we can just skip this because we're
  // going to (hopefully) show the whole form anyway.
  if (!hasPreviousSignon) {
    return null;
  }

  const alternativeLogins = 'log in with Google, Facebook, or Microsoft.';

  if (signonIdentity?.signupType === SIGNUP_TYPE_EMAIL) {
    return (
      <p>
        Or{' '}
        <LinkPreserveParamsContainer
          href="/app/login/?forceLoginDisplayForm=true"
          kind="blue"
        >
          {alternativeLogins}
        </LinkPreserveParamsContainer>
      </p>
    );
  }

  // (else) the signup type is Google SSO
  return (
    <p>
      Or{' '}
      <LinkPreserveParamsContainer
        href="/app/login/?forceLoginDisplayForm=true"
        kind="blue"
      >
        use your email address to log in.
      </LinkPreserveParamsContainer>
    </p>
  );
};

const renderWelcomeBackFor = (email: string, params: {}) => ({
  onResetFormState,
}: {
  onResetFormState: () => void,
}) => {
  return (
    <p>
      Welcome back {email}!{' '}
      <Link
        href={`/app/login?${makeQueryString(params, {})}`}
        kind="blue unbreakable"
        onClick={onResetFormState}
      >
        Not you?
      </Link>
    </p>
  );
};

const Footer = ({
  next,
  shouldShowEntireForm,
  signonIdentity = {},
}: {
  next: string,
  shouldShowEntireForm: boolean,
  signonIdentity?: Object,
}) => {
  const signUpUrl = urlWithNext('/sign-up/', next);
  return (
    <React.Fragment>
      {signonCta(shouldShowEntireForm, signonIdentity)}
      <p>
        Don’t have a Zapier account yet?{' '}
        <LinkPreserveParamsContainer href={signUpUrl} kind="blue">
          Sign Up
        </LinkPreserveParamsContainer>
      </p>
    </React.Fragment>
  );
};

type LogInFormContainerPropsType = {
  invitationEmail: string,
  query: Object,
  isInviting?: boolean,
  signonIdentity?: Object,
};

export const mapStateToProps = (
  state: Object,
  props: LogInFormContainerPropsType
) => {
  const selectedApis = selectSelectedApis(state);
  const currentUrl = selectCurrentUrl(state);
  const useCaseSlug = selectUseCaseSlug(state);

  const page = state.login.loginPage;
  const result = page.result || {};
  const errors = _.get(result, ['details', 'errors']);
  const fieldErrors = toFieldErrorsObj(errors);
  const isCaptchaRequired = _.has(fieldErrors, CAPTCHA_REQUIRED_FIELD_ERROR);
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;
  const isReauth = props.query.reauth;
  const emailFromQueryParams = props.query.email;

  const signUpEmail = props.isInviting
    ? props.invitationEmail
    : page.form.email ||
      (!page.touchedFields.email && emailFromQueryParams) ||
      '';

  const shouldShowPasswordField =
    // This forces the user to wait for the email field to be submitted
    // before showing the password field, this is mostly a problem
    // when someone has a record in a password manager that only
    // has a password but no email address (without this flag
    // as soon as you started typing an email it would switch
    // to the password screen)
    ((!page.waitForEmailSubmit || page.submittedFields.email) &&
      // Require that the user has a value in the email field && has
      // touched the password field, (to automatically show password
      // field when autofill has filled the email and password)
      signUpEmail &&
      page.touchedFields.password) ||
    // Show the email field if there is an error with the email
    (isBadCredentials(result.type) && !fieldErrors.email);

  const shouldShowEmailField = !shouldShowPasswordField;

  const { next, forceLoginDisplayForm } = state.router.current.query || {};
  const shouldDisplayEntireLoginForm =
    forceLoginDisplayForm === 'true' || isReauth;
  let shouldShowLoginForm = true;
  let facebookSSOEnabled = shouldShowEmailField;
  let googleSSOEnabled = shouldShowEmailField && !isInIframe();
  let microsoftSSOEnabled = shouldShowEmailField && !isInIframe();

  /*
    Returning user logic here - these cases cover if a user was logged out due to an expired session,
    if they were we need to enable/disable certain elements of the login form, which we only do on the
    home page.
  */

  // If the user signed up with either email or Facebook SSO, we should disable Google SSO and Microsoft SSO.
  if (
    props.signonIdentity?.signupType === SIGNUP_TYPE_EMAIL ||
    props.signonIdentity?.signupType === SIGNUP_TYPE_FACEBOOK
  ) {
    googleSSOEnabled = false;
    microsoftSSOEnabled = false;
  }

  // If the user signed up with either email or Google SSO, we should disable Facebook SSO and Microsoft SSO.
  if (
    props.signonIdentity?.signupType === SIGNUP_TYPE_EMAIL ||
    props.signonIdentity?.signupType === SIGNUP_TYPE_GOOGLE
  ) {
    facebookSSOEnabled = false;
    microsoftSSOEnabled = false;
  }

  // If the user signed up with either email or Microsoft SSO, we should disable Facebook SSO and Google SSO.
  if (
    props.signonIdentity?.signupType === SIGNUP_TYPE_EMAIL ||
    props.signonIdentity?.signupType === SIGNUP_TYPE_MICROSOFT
  ) {
    facebookSSOEnabled = false;
    googleSSOEnabled = false;
  }

  if (props.signonIdentity?.signupType === SIGNUP_TYPE_GOOGLE) {
    shouldShowLoginForm = false;
  }

  if (shouldDisplayEntireLoginForm) {
    shouldShowLoginForm = true;
    googleSSOEnabled = !isInIframe(); // Whether we forcibly show everything or not, this still won't work in an iframe
    facebookSSOEnabled = true;
    microsoftSSOEnabled = !isInIframe(); // Whether we forcibly show everything or not, this still won't work in an iframe
  }

  const currentAccountEmail = Profile.selectors.email(
    Profile.selectors.all.currentProfile(state)
  );

  const isLoggedIn =
    !!currentAccountEmail &&
    !hasLastSessionTimedOut &&
    !page.isSubmitting &&
    result.type !== ResultTypes.IS_VALID_LOGIN;

  const submitText = isLoggedIn
    ? `Continue as ${currentAccountEmail}`
    : 'Continue';
  const googleSSOButtonText = `${submitText} with Google`;
  const facebookSSOButtonText = `${submitText} with Facebook`;
  const microsoftSSOButtonText = `${submitText} with Microsoft`;

  const hasPreviousSignon = Boolean(props.signonIdentity?.email);
  const welcomeMessage = hasPreviousSignon ? 'Welcome Back!' : '';
  const hasGoogleOneTapError = Profile.selectors.all.hasGoogleOneTapError(
    state
  );

  const apiMessage = hasFieldErrors
    ? {}
    : formattedMessageForResult(result.type, {
        hasSessionTimedOut: hasLastSessionTimedOut(state),
        hasGoogleOneTapError,
      });

  const canSubmit = !page.isSubmitting && signUpEmail;

  const loginFields = [
    {
      autoFocus: true,
      classes: { '--hidden': !shouldShowEmailField },
      name: 'email',
      type: 'email',
      placeholder: 'Email',
      disabled: props.isInviting || !shouldShowEmailField,
      value: signUpEmail,
      'aria-required': true,
      'aria-invalid': !!fieldErrors.email,
    },
    shouldShowEmailField &&
      fieldErrors.email && {
        type: 'error',
        name: 'email',
        message: fieldErrors.email,
      },
    {
      autoFocus: shouldShowPasswordField,
      classes: { '--hidden': !shouldShowPasswordField },
      label: 'Password',
      name: 'password',
      type: 'password',
      placeholder: 'Password',
      value: page.form.password,
      'aria-required': true,
      'aria-invalid': !!fieldErrors.password,
    },
    shouldShowPasswordField &&
      fieldErrors.password &&
      page.submittedFields.password && {
        type: 'error',
        name: 'password',
        message: fieldErrors.password,
      },
  ].filter(Boolean);

  const description = isLoggedIn ? (
    <ContinueDescription email={currentAccountEmail} next={next} />
  ) : shouldShowPasswordField ? (
    renderWelcomeBackFor(signUpEmail, props.query)
  ) : null;

  return {
    ...props,
    selectedApis,
    useCaseSlug,
    currentUrl,
    shouldRender: true,
    title: 'Log In',
    submitText,
    canSubmit,
    welcomeMessage: shouldDisplayEntireLoginForm ? null : welcomeMessage,
    didSucceed: isValidLogin(result.type),
    isSubmitting: page.isSubmitting,
    fields: isLoggedIn ? undefined : loginFields,
    description,
    helpfulMessage: !isLoggedIn && shouldShowPasswordField && (
      <HelpfulLoggedOut />
    ),
    footer: isReauth ? null : isLoggedIn ? (
      <LoginDifferent next={next} />
    ) : (
      <Footer
        next={next}
        shouldShowEntireForm={shouldDisplayEntireLoginForm}
        signonIdentity={props.signonIdentity}
      />
    ),
    message: apiMessage.text,
    messageType: apiMessage.type,
    // Respect `next` coming from a `?next=` query param, but if it doesn't exist, use a good default
    next: next || DEFAULT_LOGIN_URL,
    hasGoogleOneTapError,
    googleSSOEnabled,
    googleSSOButtonText,
    facebookSSOEnabled,
    facebookSSOButtonText,
    microsoftSSOEnabled,
    microsoftSSOButtonText,
    shouldShowLoginForm,
    isCaptchaRequired,
    // For google one-tap, we respect `next` coming from a `?next=` query param, but we can't have a static default `next`, because
    // for google one-tap, the default `next` is dynamic (if the user signed up via one-tap but an account already existed with that
    // e-mail address, or if it's a completely new account)
    googleOneTapNext: next || null,
    disableGoogleOneTap: true,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps) => {
  return {
    onLoad() {
      // If an email is provided in the query param, then set the initial value
      // of the form email field to this value.
      const email = ownProps.router.current.query.email
        ? ownProps.router.current.query.email
        : undefined;
      const { forceLoginDisplayForm } =
        ownProps.router.current.query || 'false';

      if (!forceLoginDisplayForm && email) {
        dispatch(LoginActions.updateForm('loginPage', 'email', email));
      }

      // we need to clear the form if forceLoginDisplayForm=true is set in the query parameter
      if (forceLoginDisplayForm === 'true') {
        dispatch(LoginActions.updateForm('loginPage', 'email', ''));
      }
    },
    onChange: (value, name) => {
      dispatch(LoginActions.updateForm('loginPage', name, value));
    },
    onResetFormState: () => {
      dispatch(LoginActions.resetForm('loginPage'));
      // Reset the state of the One Tap entitiy. This will ensure that errors
      // don't carry over when a user navigates back to the login / signup form.
      dispatch(
        Profile.actions.resetEntityStatus(Profile.GOOGLE_ONE_TAP_NEW_ENTITY_ID)
      );
    },
    onSubmit: (fields, next, captchaValue) => {
      if (!fields) {
        dispatch(routeTo(next, maybeUnsafeRouteOptions()));
        return;
      }
      const email = _.get(_.find(fields, { name: 'email' }), ['value']);
      const password = _.get(_.find(fields, { name: 'password' }), ['value']);
      const captcha = captchaValue || null;
      dispatch(LoginActions.login(email, password, captcha, next));
    },
    onGoogleOneTapSubmit(idToken, googleOneTapNext) {
      dispatch(
        LoginActions.attemptOneTapGoogleSignup(idToken, googleOneTapNext)
      );
    },
    track(message, data = null) {
      dispatch(TrackActions.track(message, data));
    },
  };
};

function LoginFormContainerWrapper(props: LogInFormContainerPropsType) {
  return <LoginFormContainer {...props} signonIdentity={null} />;
}

const LoginFormContainer = _.flowRight(
  connect(mapStateToProps, mapDispatchToProps),
  hocify(MaybeLoad),
  maybeRender(props => props.shouldRender)
)(LoginForm);

LoginFormContainer.displayName = 'LoginFormContainer';

export default LoginFormContainerWrapper;
